import {
  MutationState,
  useIsMutating,
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CreateUpdateDraftDataDto,
  DraftDataDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_FORM_SUBMISSIONS } from "../domain";
import { ACTION_GET_FORM_DRAFT_DATA } from "../GetFormDraftData/useGetFormDraftData";

import { submissionsApi } from "@api/services";
import { ACTION_GET_CSOB_FORMS_METADATA, DOMAIN_CSOB, roleGuard } from "@api";

export const ACTION_CREATE_UPDATE_DRAFT_DATA = "CREATE_UPDATE_DRAFT_DATA";

const allowedRoles = [UserRole.Owner, UserRole.Manager, UserRole.Respondent];

const createUpdateDraftData = async (
  params: CreateUpdateDraftDataDto,
): Promise<DraftDataDto> => {
  const response =
    await submissionsApi.submissionsControllerCreateUpdateDraftData(params);

  return response.data;
};

export const useCreateUpdateDraftData = (
  user: UserProfileDto | null,
  formId: string,
): {
  createUpdateDraftData: (params: CreateUpdateDraftDataDto) => void;
  createUpdateDraftDataAsync: (
    params: CreateUpdateDraftDataDto,
  ) => Promise<DraftDataDto>;
  createUpdateDraftDataEnabled: boolean;
  isSuccessCreateUpdateDraftData: boolean;
  isPendingCreateUpdateDraftData: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_CREATE_UPDATE_DRAFT_DATA],
    mutationFn: enabled ? createUpdateDraftData : undefined,
    onSuccess(data) {
      const queryKey = [DOMAIN_FORM_SUBMISSIONS, ACTION_GET_FORM_DRAFT_DATA, formId];

      queryClient.setQueryData(
        queryKey,
        (currentData: DraftDataDto[] | undefined) => {
          if (!currentData) return [data];

          const dataMap = new Map(currentData.map((item) => [item.id, item]));

          dataMap.set(data.id, data);

          return Array.from(dataMap.values());
        },
      );

      // Invalidate csob froms
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_CSOB, ACTION_GET_CSOB_FORMS_METADATA],
      });
    },
    retry: 2,
  });

  return {
    createUpdateDraftData: mutate,
    createUpdateDraftDataAsync: mutateAsync,
    createUpdateDraftDataEnabled: enabled,
    isSuccessCreateUpdateDraftData: isSuccess,
    isPendingCreateUpdateDraftData: isPending,
  };
};

export const useIsMutatingDraftData = (): boolean => {
  return (
    useIsMutating({
      mutationKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_CREATE_UPDATE_DRAFT_DATA],
    }) > 0
  );
};

export const watchDraftDataQuestionState = (
  questionCodes: string[],
  parentQuestionCode?: string,
): MutationState<unknown, Error, unknown, unknown> | undefined => {
  const state = useMutationState({
    filters: {
      mutationKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_CREATE_UPDATE_DRAFT_DATA],
    },
  });

  const questionState = state.findLast((s) => {
    const params = s.variables as CreateUpdateDraftDataDto;

    if (parentQuestionCode) {
      return (
        params.parentQuestionCode === parentQuestionCode &&
        questionCodes.includes(params.questionCode)
      );
    }

    return questionCodes.includes(params.questionCode);
  });

  return questionState;
};
